<template>
  <router-view />
  <ToastNotification ref="toastRef" :message="toastMessage" :type="toastType" :cart-items="cartItems"
    :duration="3000" />
</template>

<script>
import { provide, ref, onMounted, watch } from "vue";
import ToastNotification from "@/components/ToastNotification.vue";

export default {
  name: "App",
  components: { ToastNotification },

  setup() {
    // --- TOAST ---
    const toastMessage = ref("");
    const toastType = ref("success");
    const toastRef = ref(null);

    const showToast = (message, type = "info") => {
      toastMessage.value = message;
      toastType.value = type;
      toastRef.value?.show();
    };

    // --- CART ---
    const cartItems = ref([]);

    const loadCartFromStorage = () => {
      try {
        const stored = localStorage.getItem("cartItems");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            cartItems.value = parsed.map((item) => ({
              ...item,
              price: parseFloat(item.price) || 0,
              quantity: parseInt(item.quantity) || 1,
              variant_id: item.variant_id || null,
              variant_label: item.variant_label || null,
            }));
          }
        }
      } catch (e) {
        console.error("Error loading cart from storage:", e);
      }
    };

    const saveCartToStorage = () => {
      try {
        localStorage.setItem("cartItems", JSON.stringify(cartItems.value));
      } catch (e) {
        console.error("Error saving cart to storage:", e);
      }
    };

    watch(cartItems, saveCartToStorage, { deep: true });

    const addToCart = (product) => {
      const price =
        parseFloat(product.discount_price || product.final_price || product.price) || 0;
      const image =
        product.primary_image || product.image || "/assets/medical.jpeg";
      const variantId = product.variant_id || null;
      const variantLabel = product.variant_label || null;

      // Match by both product id AND variant_id so different variants are separate cart entries
      const existing = cartItems.value.find(
        (i) => i.id === product.id && i.variant_id === variantId
      );

      if (existing) {
        existing.quantity += 1;
      } else {
        cartItems.value.push({
          id: product.id,
          name: product.name,
          price,
          image,
          quantity: 1,
          selected: true,
          variant_id: variantId,
          variant_label: variantLabel,
        });
      }
      showToast("Product added to cart", "success");
    };

    const removeFromCart = (productId, variantId = undefined) => {
      if (variantId !== undefined) {
        // Remove specific variant entry
        cartItems.value = cartItems.value.filter(
          (i) => !(i.id === productId && i.variant_id === variantId)
        );
      } else {
        // Legacy: remove by product id only (removes first match)
        cartItems.value = cartItems.value.filter((i) => i.id !== productId);
      }
      showToast("Product removed from cart", "info");
    };

    const updateQuantity = (productId, quantity, variantId = undefined) => {
      const item = cartItems.value.find((i) => {
        if (variantId !== undefined) {
          return i.id === productId && i.variant_id === variantId;
        }
        return i.id === productId;
      });
      if (item) {
        item.quantity = quantity;
        if (item.quantity <= 0) removeFromCart(productId, item.variant_id);
      }
    };

    const clearCart = () => {
      cartItems.value = [];
    };

    // --- WISHLIST ---
    const wishlistItems = ref([]);

    const addToWishlist = (prod) => {
      if (!wishlistItems.value.some((i) => i.id === prod.id)) {
        wishlistItems.value.push({
          id: prod.id,
          name: prod.name,
          price:
            parseFloat(prod.discount_price || prod.final_price || prod.price) || 0,
          image: prod.primary_image || prod.image || "/assets/medical.jpeg",
        });
      }
      showToast("Product added to wishlist", "success");
    };

    const removeFromWishlist = (productId) => {
      wishlistItems.value = wishlistItems.value.filter((i) => i.id !== productId);
      showToast("Product removed from wishlist", "info");
    };

    onMounted(() => {
      loadCartFromStorage();
    });

    // --- PROVIDE STATES ---
    provide("cartState", {
      items: cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      showToast,
    });
    provide("wishlistState", {
      items: wishlistItems,
      addToWishlist,
      removeFromWishlist,
    });

    return {
      toastMessage,
      toastType,
      toastRef,
      cartItems,
      wishlistItems,
    };
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: Arial, Helvetica, sans-serif;
  background-color: #fff;
  color: #000;
  overflow-x: hidden;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

a:hover {
  text-decoration: underline;
  color: #42b983;
}
</style>